import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createCheckboxField } from './createCheckboxField';

/**
 * A React component function that generates an array of field configurations
 * for parking and installation information in a form. It takes into account the
 * type of dwelling to selectively hide or show certain fields. It utilizes the
 * `useTranslation` hook from `react-i18next` to provide translations for the label
 * of each checkbox.
 *
 * @param {string} typeOfDwelling - The type of dwelling to base the visibility
 * of certain fields on. Can be 'HOUSE' or other values to determine the hidden
 * property of residence field.
 * @param {Object} form - The form object from Ant Design to reset certain fields
 *
 * @returns {Object} An object containing the following properties:
 * - `parkingAndInstallationInformationsFields`: An array of objects, each
 * representing a field configuration for a checkbox input capturing various parking
 * and installation details. The configurations include properties such as `hidden`,
 * `noLabel`, `name`, `input`, and `valuePropName`.
 *
 * @function
 * @example
 *
 * import ParkingAndInstallationInformations from './ParkingAndInstallationInformations';
 *
 * const { parkingAndInstallationInformationsFields } = ParkingAndInstallationInformations('HOUSE');
 *
 * // Now, parkingAndInstallationInformationsFields can be used to generate a form or part of a form
 */
const ParkingAndInstallationInformations = (typeOfDwelling, form) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (typeOfDwelling === 'HOUSE') {
      form.resetFields([
        [
          'petsitting_address',
          'type_of_storage_for_pensioner',
          'parking_and_installation',
          'parking',
          'residence'
        ]
      ]);
    }
  }, [typeOfDwelling, form]);

  const parkingAndInstallationInformationsFields = [
    createCheckboxField(
      ['parking_and_installation', 'parking'],
      'residence',
      t,
      { hidden: typeOfDwelling === 'HOUSE' || typeOfDwelling === '' }
    ),
    createCheckboxField(['parking_and_installation', 'parking'], 'private', t),
    createCheckboxField(['parking_and_installation', 'parking'], 'garden', t),
    createCheckboxField(['parking_and_installation', 'parking'], 'street', t),
    createCheckboxField(['parking_and_installation', 'parking'], 'charged', t),
    createCheckboxField(['parking_and_installation', 'parking'], 'garage', t),
    createCheckboxField(
      ['parking_and_installation', 'parking'],
      'no_parking',
      t
    ),
    createCheckboxField(
      ['parking_and_installation', 'installation'],
      'alarm',
      t
    ),
    createCheckboxField(
      ['parking_and_installation', 'installation'],
      'telesurveillance',
      t
    )
  ];

  return {
    parkingAndInstallationInformationsFields
  };
};

export default ParkingAndInstallationInformations;
